// Console de l'éditeur (D-081) : palier et prix (D-078), saison, formule, nom d'un club lu à la
// FFBB, équipes regroupées à partir des engagements. Fonctions pures (testées).

/** Paliers par nombre de licenciés (D-078) ; au-delà de 1 000 : devis (null). */
export const TIERS = [100, 200, 300, 500, 1000] as const;
export type Tier = typeof TIERS[number];
export const TIER_PRICE: Record<Tier, number> = { 100: 100, 200: 150, 300: 200, 500: 250, 1000: 350 };   // € TTC par saison

export function tierFor(licencies: number | null | undefined): Tier | null {
  if (!licencies || licencies < 1) return null;
  return TIERS.find(t => licencies <= t) ?? null;
}
export const priceOf = (tier: number | null | undefined): number | null => (tier && tier in TIER_PRICE ? TIER_PRICE[tier as Tier] : null);

/** Saison sportive en cours : de septembre à juin (à partir de juillet, la suivante). */
export function seasonFor(d: Date): { name: string; starts_on: string; ends_on: string } {
  const y = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
  return { name: `${y}-${y + 1}`, starts_on: `${y}-09-01`, ends_on: `${y + 1}-06-30` };
}

export type Plan = 'demo' | 'trial' | 'pilot' | 'client';
export const PLAN_LABEL: Record<Plan, string> = { demo: 'Démo', trial: 'Essai', pilot: 'Pilote', client: 'Client' };

/** Échéance proposée : un essai dure 30 jours ; un pilote et un client, jusqu'à la fin de la saison. */
export function planUntil(plan: Plan, today: Date): string | null {
  if (plan === 'demo') return null;
  if (plan === 'trial') { const d = new Date(today); d.setDate(d.getDate() + 30); return iso(d); }
  return seasonFor(today).ends_on;
}
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const SMALL = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'et', 'en', 'sur', 'a', 'à', 'au', 'aux']);
/** « BASKET CLUB L'ISLOIS » → « Basket Club L'Islois » (la FFBB écrit tout en capitales). */
export function titleCase(s: string): string {
  return s.trim().toLowerCase().split(/\s+/).map((w, i) => {
    if (i > 0 && SMALL.has(w)) return w;
    return w.replace(/(^|[-'’])(\p{L})/gu, (_, sep: string, c: string) => sep + c.toUpperCase());
  }).join(' ');
}

/** Sigle proposé : les initiales des mots qui comptent (« Basket Club de Rivebelle » → « BCR »). */
export function sigle(name: string): string {
  return name.split(/[\s-]+/).filter(w => /^\p{L}/u.test(w) && !SMALL.has(w.toLowerCase()))
    .map(w => w.charAt(0).toUpperCase()).join('').slice(0, 6);
}

/** Un engagement FFBB, tel que lu par lib/ffbb.ts. */
export interface Eng {
  engagement_id: string; poule_id: string; poule_name?: string | null; competition_name: string; competition_code: string | null;
  competition_type: string; category: string | null; sex: string | null; team_name: string | null; team_number: number | null;
}
export interface TeamDraft {
  key: string; name: string; category: string | null; gender: 'F' | 'M' | 'X'; on: boolean;
  links: {
    squad?: string; engagement_id: string; poule_id: string; competition_name: string; competition_code: string | null; competition_type: string;
    ffbb_team_name: string | null; team_number?: number | null; poule_name?: string | null;
  }[];
}

const CAT_ORDER = ['SE', 'U21', 'U20', 'U18', 'U17', 'U15', 'U13', 'U11', 'U9', 'U7'];
const catName = (cat: string | null) => (cat === 'SE' ? 'Seniors' : cat ?? 'Équipe');

/** Nom d'équipe : « Seniors filles », « U15 garçons », « U9 mixte ». */
export function teamName(cat: string | null, g: 'F' | 'M' | 'X'): string {
  return `${catName(cat)} ${g === 'F' ? 'filles' : g === 'M' ? 'garçons' : 'mixte'}`;
}
/** Nom d'une équipe engagée dans un groupe : SF1, SG2, U13F1… */
export function squadName(cat: string | null, g: 'F' | 'M' | 'X', n: number): string {
  if (cat === 'SE') return `S${g === 'F' ? 'F' : g === 'M' ? 'G' : 'X'}${n}`;
  return `${cat ?? ''}${g === 'X' ? '' : g}${n}`;
}

/**
 * Les engagements d'un club, regroupés en équipes Arceau : une équipe par catégorie et sexe ; si le
 * club engage plusieurs équipes dans la même catégorie (au moins deux numéros distincts), elles
 * deviennent les équipes engagées du groupe (SF1, SF2 — D-042). Tous les engagements d'un numéro
 * (championnat, coupe, phases finales) vont à son équipe engagée ; un engagement sans numéro
 * (brassage, plateau, championnat unique) va au groupe entier (D-090).
 */
export function groupEngagements(list: Eng[]): TeamDraft[] {
  const by = new Map<string, Eng[]>();
  for (const e of list) {
    const g = e.sex === 'F' ? 'F' : e.sex === 'M' ? 'M' : 'X';
    const k = `${e.category ?? '?'}|${g}`;
    by.set(k, [...(by.get(k) ?? []), e]);
  }
  const out: TeamDraft[] = [];
  for (const [k, es] of by) {
    const [cat, g] = k.split('|') as [string, 'F' | 'M' | 'X'];
    const category = cat === '?' ? null : cat;
    // Numéro 0 ou absent (brassages) : pas une équipe engagée à part.
    const num = (e: Eng) => (e.team_number && e.team_number > 0 ? e.team_number : null);
    const numbers = [...new Set(es.map(num).filter((n): n is number => n != null))].sort((a, b) => a - b);
    const squads = numbers.length > 1;
    out.push({
      key: k, name: teamName(category, g), category, gender: g, on: true,
      links: es.map(e => ({
        ...(squads && num(e) != null ? { squad: squadName(category, g, num(e)!) } : {}),
        engagement_id: e.engagement_id, poule_id: e.poule_id, competition_name: e.competition_name,
        competition_code: e.competition_code, competition_type: e.competition_type, ffbb_team_name: e.team_name,
        team_number: num(e), poule_name: e.poule_name ?? null,
      })),
    });
  }
  const rank = (c: string | null) => { const i = CAT_ORDER.indexOf(c ?? ''); return i < 0 ? 99 : i; };
  return out.sort((a, b) => rank(a.category) - rank(b.category) || a.gender.localeCompare(b.gender));
}

/** Santé de la FFBB vue de la console (D-113) : « bad » au moins 3 échecs sur au moins 2 appareils en une heure
 *  (l'éditeur reçoit aussi une notification), « warn » des échecs ces 24 h, « ok » sinon. */
export function ffbbHealthState(h: { failures_1h: number; devices_1h: number; failures_24h: number }): 'bad' | 'warn' | 'ok' {
  return h.failures_1h >= 3 && h.devices_1h >= 2 ? 'bad' : h.failures_24h > 0 ? 'warn' : 'ok';
}
